import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ProviderOrganizationResult, Provider } from '@app/src/types/organizations'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import BaseProviderScene from './BaseProviderScene'
import { ProviderTabs } from './ProvidersScene'
import { Alert, Button } from '@mui/material'
import { DoneAllOutlined } from '@mui/icons-material'
import { useProvidersModal, ProvidersModalPurpose } from '@app/src/context/ProvidersModalContext'
import type { ResourceCollectionSceneProps } from '../../ResourceCollectionScene'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'

type NotConnectedProvidersSceneProps = {
  relatedContentFilter?: FilterGroup[]
  allowedFilters: string[]
} & Omit<ResourceCollectionSceneProps, 'title'>

const NotConnectedProvidersScene: React.FC<NotConnectedProvidersSceneProps> = ({
  relatedContentFilter = [],
  allowedFilters,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()

  const implicitFilters = [
    {
      name: 'linked',
      filters: [
        {
          operator: Operators.EqualTo,
          value: false,
        },
      ],
    },
    ...relatedContentFilter,
  ]

  const { items: unlinkedProviders = [] } = useFetchCollectionWithPost<Provider>({
    key: [FetchKey.ProviderCollection],
    endpoint: endpoints.providersCollection,
    payload: {
      filter: implicitFilters,
      include: ['referralContact', 'organization'],
    },
  })

  const unlinkedProviderIds = unlinkedProviders.map(p => p.id)

  const { data: exactMatches = [], isLoading: isLoadingExactMatches } = useFetchResourceWithPost<
    Array<ProviderOrganizationResult>,
    number[]
  >({
    endpoint: endpoints.bulkFindExactMatches,
    key: [FetchKey.ExactMatchExists, ...unlinkedProviderIds],
    body: unlinkedProviderIds,
    options: {
      enabled: Boolean(unlinkedProviderIds.length),
    },
  })

  const showExactMatchesBanner = !!exactMatches.length && !isLoadingExactMatches
  const exactMatchesCount = exactMatches.length
  const { handleOpenConnectDialog } = useProvidersModal()

  useEffect(() => {
    if (showExactMatchesBanner) {
      trackEvent({
        name: AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.BannerViewed,
        eventProps: {
          exact_matches_count: exactMatchesCount,
          source: 'not_connected_providers_page',
        },
      })
    }
  }, [showExactMatchesBanner, exactMatchesCount, trackEvent])

  const handleViewClick = () => {
    trackEvent({
      name: AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.BannerClicked,
      eventProps: {
        exact_matches_count: exactMatchesCount,
        source: 'not_connected_providers_page',
      },
    })
    handleOpenConnectDialog(unlinkedProviders, ProvidersModalPurpose.ConnectOnlyFlow, exactMatches)
  }

  return (
    <BaseProviderScene
      implicitFilters={implicitFilters}
      allowedFilters={allowedFilters}
      resourceView={ProviderTabs.NotConnected}
      banner={
        showExactMatchesBanner && (
          <Alert
            icon={<DoneAllOutlined />}
            severity="info"
            sx={{
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              mr: 4,
            }}
            action={
              <Button variant="text" onClick={handleViewClick}>
                {formatMessage({ id: 'general.view' })}
              </Button>
            }
            data-testid="exact-matches-banner"
          >
            {formatMessage(
              {
                id: 'resourceCollections.providers.connectOnlyFlow.connectionStatuses.notConnected.exactMatchesBanner',
              },
              { count: exactMatchesCount },
            )}
          </Alert>
        )
      }
      {...props}
    />
  )
}
export default NotConnectedProvidersScene
